import React from "react";
import dayjs from "dayjs";
import { Row, Col } from "rsuite";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import GiftIcon from "../../assets/img/gift.svg";
import CoffeeIcon from "../../assets/img/coffee.svg";
import CoinHandIcon from "../../assets/img/coin-hand.svg";
import {
  getAppliesToRule,
  getDiscountValueFromRule,
  safeParseJson,
  normalizeToUTCMidnight,
} from "../../constants/utils";
import DiscountPopover from "./DiscountPopover";
import DerivedRatePopover, {
  getDerivedRateDiscountDisplay,
} from "./DerivedRatePopover";
import StackedPromotionSummary from "./StackedPromotionSummary";
import { selectSelectedRateplanCurrency } from "../../slices/rateplanSlice";
import { useIsPilotHotel } from "../../contexts/PropertyStoreContext";
import "./promotion-summary.scss";

const PromotionSummary = ({ promos, rateplan, date, derivedRates = [] }) => {
  const currency = useSelector(selectSelectedRateplanCurrency);
  const isPilotHotel = useIsPilotHotel();
  const {
    data: { isRefundable, isBreakfastIncluded },
  } = rateplan ?? { data: {} };
  const specificDate = new Date(date);
  const dayOfWeek = specificDate.getDay() + 1;

  if (isPilotHotel) {
    return (
      <StackedPromotionSummary
        promos={promos}
        rateplan={rateplan}
        date={date}
        derivedRates={derivedRates}
      />
    );
  }

  const filteredPromos = promos.filter((promo) => {
    const rule = safeParseJson(promo.ruleJson);
    const discountVO = rule?.ruleVO?.discountVO;

    // Check if discountTypeDescription is "Discount by Day of Week"
    if (discountVO?.discountTypeDescription === "Discount by Day of Week") {
      const percentDiscountMap = discountVO?.percentDiscountMap;

      // Check if dayOfWeek is valid and if the discount for the specific day is zero
      if (
        // Invalid dayOfWeek
        percentDiscountMap[dayOfWeek] === 0 // Discount is zero for the specific day
      ) {
        return false; // Exclude this promo
      }
    }

    return true; // Include this promo
  });
  return (
    <div className="promotion-container">
      {filteredPromos?.length
        ? filteredPromos.map((promo, index) => {
            const { id, ruleName, ruleJson } = promo;
            const rule = safeParseJson(ruleJson);
            const discountValue = getDiscountValueFromRule(
              rule,
              currency,
              dayOfWeek
            );
            const { stayStartDate, stayEndDate } = getAppliesToRule(rule);
            const utcStartDateMidnight = normalizeToUTCMidnight(stayStartDate);
            const utcEndDateMidnight = normalizeToUTCMidnight(stayEndDate);
            if (
              (date && stayStartDate && date.isBefore(utcStartDateMidnight)) ||
              (date && stayEndDate && date.isAfter(utcEndDateMidnight))
            ) {
              return null;
            }

            return (
              <Row key={id}>
                <Col xs={3}>
                  {index === 0 ? (
                    <div className="promotion-wrapper-icon">
                      <img
                        src={GiftIcon}
                        className="promotion-icon"
                        alt="promotions"
                      />
                    </div>
                  ) : null}
                </Col>
                <Col xs={14} className="promotion-label text-truncate">
                  {ruleName}
                </Col>
                <Col xs={4} className="discount-value">
                  {discountValue}
                </Col>
                <Col xs={3}>
                  <DiscountPopover
                    rule={rule}
                    promo={promo}
                    discountValue={discountValue}
                    dayOfWeek={dayOfWeek}
                  />
                </Col>
              </Row>
            );
          })
        : null}
      {derivedRates?.length
        ? derivedRates.map((dr, index) => {
            const discountValue = getDerivedRateDiscountDisplay(
              dr.priceConfig,
              dayOfWeek,
              currency
            );

            return (
              <Row key={dr.id}>
                <Col xs={3}>
                  {index === 0 ? (
                    <div className="promotion-wrapper-icon">
                      <img
                        src={GiftIcon}
                        className="promotion-icon"
                        alt="promotions"
                      />
                    </div>
                  ) : null}
                </Col>
                <Col xs={14} className="promotion-label text-truncate">
                  {dr.name}
                </Col>
                <Col xs={4} className="discount-value">
                  {discountValue}
                </Col>
                <Col xs={3}>
                  <DerivedRatePopover
                    derivedRate={dr}
                    dayOfWeek={dayOfWeek}
                    currency={currency}
                  />
                </Col>
              </Row>
            );
          })
        : null}
      {isBreakfastIncluded ? (
        <Row className="mt-4">
          <Col xs={3}>
            <div className="breakast-wrapper-icon">
              <img
                src={CoffeeIcon}
                alt="Food icon"
                className="breakfast-icon"
              />
            </div>
          </Col>
          <Col xs={21} className="promotion-label">
            Bed & Breakfast included
          </Col>
        </Row>
      ) : null}

      {isRefundable ? (
        <Row className="mt-4">
          <Col xs={3}>
            <div className="coin-hand-wrapper-icon">
              <img
                src={CoinHandIcon}
                alt="promotion icon"
                className="coin-hand-icon"
              />
            </div>
          </Col>
          <Col xs={21} className="promotion-label">
            Refundable
          </Col>
        </Row>
      ) : null}
    </div>
  );
};

PromotionSummary.propTypes = {
  promos: PropTypes.array,
  derivedRates: PropTypes.array,
  rateplan: PropTypes.shape({
    isRefundable: PropTypes.bool,
    isBreakfastIncluded: PropTypes.bool,
  }),
  date: PropTypes.instanceOf(dayjs),
};

export default PromotionSummary;
